/**
 * Remote adaptive screener page: validate → birthday gate → resolve kind
 * (fail-visible) → one-question-at-a-time walk with per-answer save and
 * cross-device resume → per-section results.
 *
 * Every answer is persisted the moment it is given (D6); a positive self-harm
 * answer is escalated server-side on save and surfaces crisis resources here
 * immediately (D2/D14). Kind resolution never silently defaults — a lookup
 * failure shows an explicit screen (D4). Completion is decided by the server
 * (`resume.complete`), so a re-opened, finished screener shows results rather
 * than the last question.
 *
 * Lifecycle (U2): per-question skip and one-action section decline persist
 * server-side (§8.4–§8.6); the page renders the server's entry decision —
 * closed → the generic expired screen (D11), completed-but-unstarted → a
 * closing card with no results (D12), completed → crisis-first results — and
 * every post-decline advance comes from the settle response (D18). End-of-walk
 * completion re-syncs against fresh server state before presenting results,
 * and the results readback labels each section by its SERVER-recorded state
 * (§8.28/D21): band for complete, state label and no number otherwise,
 * never-reached sections omitted (U3).
 * @agentic members
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Center,
  Loader,
  Stack,
  Text,
  Paper,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  QUESTIONNAIRE_URLS,
  LIKERT_FREQUENCY_OPTIONS,
} from "@benchmark/core/constants";
import { SurveyShell } from "../SurveyShell";
import { BirthdayVerification } from "../BirthdayVerification";
import { ScreenerWelcomeScreen } from "./ScreenerWelcomeScreen";
import { LinearAssessmentView } from "../LinearAssessmentView";
import { buildSurveyResponseItem } from "../surveyFlow";
import { getAdvocateDisplayName } from "../surveyEnv";
import {
  CARE_TEAM_CONTACT_MESSAGE,
  surveyErrorToState,
} from "../surveyErrorState";
import { SurveyError } from "../api";
import {
  validateScreener,
  verifyScreenerBirthday,
  getScreenerState,
  saveScreenerAnswer,
  settleScreenerSection,
  type ScreenerSection,
  type ScreenerSectionState,
  type ScreenerStateResponse,
  type ScreenerSettleResponse,
} from "./api";
import {
  buildScreenerSteps,
  firstUnpresentedStepIndex,
  settledSectionInstrumentUrls,
  activeInstrumentItems,
  administeredInstrumentUrls,
  deriveSectionResult,
  type ScreenerAnswers,
  type ScreenerSkips,
  type ScreenerStep,
} from "./screenerFlow";
import { ScreenerResults, type ScreenerResultCard } from "./ScreenerResults";
import { deriveQuestionnaireResult } from "@benchmark/core/resolvers";
import type { Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";

type ScreenerPageProps = { token: string | null };

/** PHQ-9 / PHQ-9A self-harm items; any positive answer surfaces crisis resources. */
const SELF_HARM_LINK_IDS = new Set(["phq9-9", "phq9a-9"]);
/**
 * Answer values that count as a positive self-harm answer (Likert weight >= 1).
 *
 * The stored answer value is the selected option's `code`: a numeric string on
 * the canonical questionnaires (and on weight-first restored answers), or a
 * LOINC code (e.g. `LA6569-3`) when a live selection was made on an older
 * Medplum-authored form. Both shapes are covered: the crisis-triggering values
 * are derived from the shared Likert set (weight >= 1) plus the numeric
 * strings.
 */
const POSITIVE_SELF_HARM_VALUES = new Set<string>(
  LIKERT_FREQUENCY_OPTIONS.filter((option) => option.value >= 1).flatMap(
    (option) => [option.coding.code, String(option.value)],
  ),
);
/** Stable list form for props expecting the crisis-triggering answer values. */
const POSITIVE_SELF_HARM_VALUE_LIST = [...POSITIVE_SELF_HARM_VALUES];
/** Depression instruments whose self-harm item drives the safety escalation. */
const DEPRESSION_URLS = [QUESTIONNAIRE_URLS.PHQ9, QUESTIONNAIRE_URLS.PHQ9A];

/** Depression instrument URLs that carry a positive self-harm answer. */
const crisisInstrumentUrls = (answers: ScreenerAnswers): string[] =>
  DEPRESSION_URLS.filter((url) => {
    const row = answers[url];
    if (!row) return false;
    for (const linkId of SELF_HARM_LINK_IDS) {
      if (POSITIVE_SELF_HARM_VALUES.has(row[linkId] ?? "")) return true;
    }
    return false;
  });

/**
 * True when the administration includes a positive self-harm answer. Derived
 * from the saved answers so the crisis screen (§8.21) fires on completion even
 * if the patient continued past the inline alert.
 */
const hasCrisisAnswer = (answers: ScreenerAnswers): boolean =>
  crisisInstrumentUrls(answers).length > 0;

type WelcomeContext = {
  advocateDisplayName?: string;
};

/** Pre-walk welcome shown once on a fresh (unstarted) administration. */
type WelcomeState = {
  status: "welcome";
  sections: ScreenerSection[];
  questionnaires: Questionnaire[];
  kind?: "first-check-in" | "follow-up";
  context: WelcomeContext;
};

/** The one-question-at-a-time walk. */
type AssessmentState = {
  status: "assessment";
  sections: ScreenerSection[];
  questionnaires: Questionnaire[];
  stepIndex: number;
  context: WelcomeContext;
};

type PageState =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "expired" }
  | { status: "already-completed" }
  /** D12: the administration completed with every question skipped — no results. */
  | { status: "completed-unstarted" }
  | { status: "ineligible" }
  | { status: "error"; message: string }
  | { status: "verify-birthday" }
  | { status: "kind-error" }
  | WelcomeState
  | AssessmentState
  // The crisis screen carries no cards: results are built AFTER the patient
  // acknowledges it, from fresh server state (D18) — safety display never
  // waits on, or fails with, a results fetch.
  | { status: "crisis" }
  | {
      status: "results";
      cards: ScreenerResultCard[];
      crisisTriggered: boolean;
    };

/**
 * A save that failed for a non-terminal reason, retained for a manual,
 * non-destructive retry. Keyed by questionnaire URL so one instrument's failure
 * never clears another's (a dropped safety save must never be lost behind an
 * unrelated success).
 */
type PendingSave = {
  questionnaireUrl: string;
  questionnaires: Questionnaire[];
};

/** Restore server-saved answers into the flat answer map. */
const restoreAnswers = (state: ScreenerStateResponse): ScreenerAnswers => {
  const answers: ScreenerAnswers = {};
  for (const [url, record] of Object.entries(state.answers ?? {})) {
    const row: Record<string, string> = {};
    for (const item of record.items) {
      const answer = (item.answer?.[0] ?? {}) as {
        valueInteger?: number;
        valueCoding?: { code?: string };
      };
      // Prefer the numeric weight (`valueInteger`, always written by this
      // app's saves) over the coding code: the canonical questionnaires carry
      // numeric-string option codes, so a weight-first restore keeps rows
      // matchable against them even for answers saved under an older
      // Medplum-authored form whose codes were LOINC LA codes.
      const code =
        answer.valueInteger !== undefined
          ? String(answer.valueInteger)
          : answer.valueCoding?.code;
      if (item.linkId && code !== undefined) {
        row[item.linkId] = code;
      }
    }
    answers[url] = row;
  }
  return answers;
};

/**
 * Patient-facing labels for the non-complete recorded states (D21 defaults;
 * exact copy is Clinical-owned, PRD §18.5/§18.7). No non-complete state ever
 * shows a score or a band (D3/§8.9).
 */
const SECTION_STATE_LABELS: Partial<Record<ScreenerSectionState, string>> = {
  skipped: "Skipped — no result is shown.",
  "partial-declined":
    "Ended early — the answers given are saved, and no result is shown.",
  "partial-abandoned": "Started — not completed, so no result is shown.",
};

/**
 * A section the server recorded as complete whose band cannot be computed
 * client-side (e.g. a registry gap): the readback stays honest to the recorded
 * state — completed, result unavailable — and never shows a fabricated number
 * (D3) or a misleading "not completed" line (D18/D21). Copy is Clinical-owned
 * like every patient-facing string (§18.5).
 */
const COMPLETE_RESULT_UNAVAILABLE_LABEL =
  "Completed — the result isn't available right now.";

/** The recorded states that are omitted from the patient's own results. */
const OMITTED_SECTION_STATES = new Set<ScreenerSectionState>([
  "not-reached",
  "not-clinically-appropriate",
]);

/**
 * Build the per-section results readback from the SERVER's recorded section
 * states (§8.28, D18/D21): a `complete` section carries its band (scored
 * through the shared core resolver — the state decides WHETHER a band renders;
 * the resolver only computes it); every other recorded state shows its label
 * and no number; never-reached / not-clinically-appropriate sections are
 * omitted. Without a `sectionStates` map (a pre-lifecycle server), the MVP
 * two-state derivation is the fallback so a rollback never blanks results.
 */
const buildResultCards = (
  sections: ScreenerSection[],
  questionnaires: Questionnaire[],
  answers: ScreenerAnswers,
  sectionStates?: Record<string, ScreenerSectionState>,
): ScreenerResultCard[] => {
  const cards: ScreenerResultCard[] = [];
  for (const section of sections) {
    const recorded = sectionStates?.[section.key];
    const fallback =
      sectionStates === undefined
        ? deriveSectionResult(section, questionnaires, answers)
        : undefined;

    // Omission: the server recorded nothing patient-facing for this section
    // (or, on the fallback path, the section was never reached).
    if (sectionStates !== undefined) {
      if (recorded === undefined || OMITTED_SECTION_STATES.has(recorded)) {
        continue;
      }
    } else if (fallback && !fallback.complete && !fallback.anyAnswered) {
      continue;
    }

    const administered = administeredInstrumentUrls(
      section,
      questionnaires,
      answers,
    );
    const isComplete =
      sectionStates !== undefined
        ? recorded === "complete"
        : (fallback?.complete ?? false);
    // The scoring instrument is the LAST administered one (GAD-7 over GAD-2) —
    // set only for a complete section; a non-complete section is labeled by
    // its first administered instrument, exactly as the MVP did.
    const scoreUrl =
      sectionStates !== undefined
        ? isComplete
          ? administered[administered.length - 1]
          : undefined
        : fallback?.scoreUrl;
    const labelUrl = scoreUrl ?? administered[0];
    const q = questionnaires.find((it) => it.url === labelUrl);
    const label = q?.title ?? section.reason ?? labelUrl ?? section.key;

    if (isComplete && scoreUrl) {
      const url = scoreUrl;
      const row = answers[url] ?? {};
      const items = activeInstrumentItems(url, questionnaires, answers);
      // Score through the canonical resolver, which enforces D3/§8.9: only a
      // fully answered instrument yields a score and a band.
      const response: QuestionnaireResponse = {
        resourceType: "QuestionnaireResponse",
        status: "completed",
        questionnaire: url,
        item: items
          .filter((qi) => row[qi.linkId ?? ""] !== undefined)
          .map((qi) => buildSurveyResponseItem(qi, row[qi.linkId ?? ""])),
      };
      const score = deriveQuestionnaireResult(response).score;
      if (score) {
        cards.push({
          sectionKey: section.key,
          label,
          complete: true,
          outcome: "complete",
          total: score.value,
          maxScore: score.maxScore,
          severityDisplay: score.riskLevelDisplay,
          interpretation: score.interpretation,
        });
        continue;
      }
      // A recorded-complete section whose score cannot resolve (registry gap)
      // falls through to an honest completed-without-band card — never a
      // fabricated number (D3) and never a "not completed" line that
      // contradicts the recorded state (D18/D21).
    }

    // Only a SERVER-recorded complete section earns the honest
    // completed-without-band line; the rollback fallback keeps the exact MVP
    // label for a locally-complete-but-unscorable section (client-derived
    // "Completed" would overstate what the record backs).
    if (sectionStates !== undefined && recorded === "complete") {
      cards.push({
        sectionKey: section.key,
        label,
        complete: false,
        outcome: "complete",
        stateLabel: COMPLETE_RESULT_UNAVAILABLE_LABEL,
      });
      continue;
    }

    cards.push({
      sectionKey: section.key,
      label,
      complete: false,
      ...(recorded ? { outcome: recorded } : {}),
      stateLabel:
        (recorded && SECTION_STATE_LABELS[recorded]) ??
        "Started — not completed, so no result is shown.",
    });
  }
  return cards;
};

/**
 * Root flow for `/screener/:token`.
 * @agentic members
 */
export const ScreenerPage = ({ token }: ScreenerPageProps) => {
  const [state, setState] = useState<PageState>(
    token ? { status: "loading" } : { status: "invalid" },
  );
  const [answers, setAnswers] = useState<ScreenerAnswers>({});
  // Explicit per-question skips (§8.4), seeded from the server's `skips` map
  // and mirrored optimistically for in-sitting skips. Walk position only — the
  // recorded truth is always the server's (D18).
  const [skips, setSkips] = useState<ScreenerSkips>({});
  // Explicit decline settlements per instrument URL (D10), seeded from the
  // server and extended by settle responses; settled instruments never produce
  // walk steps (§9.15).
  const [settlements, setSettlements] = useState<Record<string, string>>({});
  // A failed section-decline call, retained for a non-destructive retry.
  const [declineError, setDeclineError] = useState<string | null>(null);
  // End-of-walk re-sync failed (results could not be confirmed against the
  // server); the walk holds with a retry rather than fabricating a readback.
  const [finalizeError, setFinalizeError] = useState(false);
  // End-of-walk re-sync in progress: the walk is frozen (no answer/skip/
  // decline) so a late mutation cannot queue behind the state fetch and be
  // overwritten by its restore before the mutation's save executes.
  const [finalizing, setFinalizing] = useState(false);
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);
  // Per-instrument save errors: a failed save of one instrument must never be
  // cleared by a later success of another (a dropped phq9-9 save would drop the
  // locked safety escalation). Keyed by questionnaire URL.
  const [saveErrors, setSaveErrors] = useState<Record<string, PendingSave>>({});
  // Instruments with a save currently in flight, so the crisis gate can tell an
  // unsettled escalation from a settled one.
  const [savingUrls, setSavingUrls] = useState<Set<string>>(new Set());
  // Crisis instruments whose save SUCCEEDED but whose acute-risk escalation the
  // server reported undelivered (escalationDelivered === false). These have no
  // save error, so they are tracked separately to keep the crisis screen from
  // presenting as done and to drive an idempotent escalation retry (D2/D14).
  const [escalationErrors, setEscalationErrors] = useState<Set<string>>(
    new Set(),
  );
  // The server reported a recorded escalation as undelivered
  // (safety.delivered === false) while the CURRENT answers decode no crisis
  // value (the self-harm answer was revised down). There is no local write to
  // retry through, but the crisis screen must still hold with a retry — the
  // retry reloads state, and the server re-drives the idempotent escalation on
  // every load (F4).
  const [serverEscalationPending, setServerEscalationPending] = useState(false);
  // The latest save write per instrument, so the crisis screen can re-drive an
  // escalation retry even when the underlying save itself succeeded.
  const writesRef = useRef<Record<string, PendingSave>>({});
  const answersRef = useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  const skipsRef = useRef(skips);
  useEffect(() => {
    skipsRef.current = skips;
  }, [skips]);
  const settlementsRef = useRef(settlements);
  useEffect(() => {
    settlementsRef.current = settlements;
  }, [settlements]);
  const stateRef = useRef<PageState>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  // The walk context (sections + questionnaires), retained so the crisis
  // screen — which carries no cards — can build results after acknowledgement.
  const walkRef = useRef<{
    sections: ScreenerSection[];
    questionnaires: Questionnaire[];
  } | null>(null);
  const finalizingRef = useRef(false);
  // A completion was requested but stalled behind unsettled saves: once the
  // last save settles cleanly, the flow re-finalizes automatically instead of
  // stranding the patient on a frozen last question or an empty walk (Bugbot
  // #1576: "last skip races the save hold" / "decline after failed save").
  const pendingFinalizeRef = useRef(false);
  // Latest finalizeWalk, reachable from enqueueSave (defined earlier).
  const finalizeRef = useRef<((crisisAcknowledged: boolean) => void) | null>(
    null,
  );
  // Latest goNext, reachable from handleAnswer (declared before goNext).
  const goNextRef = useRef<(() => void) | null>(null);
  // The page-level advance armed when a gate-opening answer on the LAST step
  // grows the walk (F15): the question component skips auto-advance on the
  // last question and its Submit control disappears once the walk grows, so
  // without this the patient has no forward control into the revealed steps.
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearAdvanceTimer = useCallback(() => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearAdvanceTimer, [clearAdvanceTimer]);
  // Save-state refs are written SYNCHRONOUSLY at each mutation (never via a
  // render-lagged mirror effect): the FIFO chain's queued consumers — a decline
  // or an end-of-walk finalize re-checking for failed saves at execution time —
  // run in the same microtask turn a save settles in, before React re-renders,
  // and a stale ref there would let a settle orphan an unsaved answer (D6).
  const saveErrorsRef = useRef<Record<string, PendingSave>>({});
  const savingUrlsRef = useRef<Set<string>>(new Set());
  const updateSaveError = useCallback(
    (url: string, write: PendingSave | undefined) => {
      const next = { ...saveErrorsRef.current };
      if (write) {
        next[url] = write;
      } else if (!(url in next)) {
        return;
      } else {
        delete next[url];
      }
      saveErrorsRef.current = next;
      setSaveErrors(next);
    },
    [],
  );
  const markSaving = useCallback((url: string, saving: boolean) => {
    const next = new Set(savingUrlsRef.current);
    if (saving) {
      next.add(url);
    } else {
      next.delete(url);
    }
    savingUrlsRef.current = next;
    setSavingUrls(next);
  }, []);
  // Serializes every save through one FIFO chain so an earlier, slower POST can
  // never land after (and overwrite) a later one (D6 / no lost answers).
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());

  // True while presenting results that the patient reached THROUGH the crisis
  // screen (they already acknowledged it), so a fresh-state apply does not
  // bounce them back onto it.
  const crisisAckRef = useRef(false);

  // The pre-walk welcome is shown once, only on a truly fresh administration.
  // Once the patient starts (or the walk is resumed with any recorded work), a
  // later re-sync must never bounce them back onto the welcome.
  const welcomeAcknowledgedRef = useRef(false);

  /**
   * Apply a fresh state response — the single routing point for every fetch
   * (initial load, re-sync, and the end-of-walk finalize), so crisis detection,
   * D12, and completion always evaluate against the SERVER's answers (D18) —
   * never a stale local snapshot (e.g. a concurrent device's self-harm answer).
   */
  const applyState = useCallback((data: ScreenerStateResponse) => {
    if (data.kindError) {
      setState({ status: "kind-error" });
      return;
    }
    // Adults-only fail-safe (D14): a non-adult composes no sections; show an
    // explicit unavailable state, never a (vacuously complete) results screen.
    if (data.eligible === false) {
      setState({ status: "ineligible" });
      return;
    }
    if (!data.sections || !data.questionnaires) {
      setState({ status: "kind-error" });
      return;
    }
    // The server's entry decision (D18): a closed administration lands on the
    // generic expired screen (D11); the client never re-derives this.
    if (data.entry === "closed") {
      setState({ status: "expired" });
      return;
    }
    const restored = restoreAnswers(data);
    setAnswers(restored);
    answersRef.current = restored;
    const restoredSkips: ScreenerSkips = data.skips ?? {};
    setSkips(restoredSkips);
    skipsRef.current = restoredSkips;
    const restoredSettlements = data.settlements ?? {};
    setSettlements(restoredSettlements);
    settlementsRef.current = restoredSettlements;

    // D12: an administration completed with every question skipped never
    // started — it produces no results (and no crisis screen: no answers
    // exist, so no crisis answer can).
    if (data.entry === "completed" && data.started === false) {
      setState({ status: "completed-unstarted" });
      return;
    }

    // A positive self-harm answer that the server could not durably escalate
    // (delivered === false) must keep the crisis screen in its retry-holding
    // state across a reopen/new device — the in-memory escalation error is gone
    // after a reload, so re-seed it here and prime the write used to re-drive
    // the idempotent escalation. The server already re-attempted delivery on
    // this load; this only holds the UI when it is still undelivered.
    const undelivered =
      data.safety?.triggered === true && data.safety.delivered === false;
    if (undelivered) {
      const unsettled = new Set<string>();
      for (const url of crisisInstrumentUrls(restored)) {
        unsettled.add(url);
        writesRef.current[url] = {
          questionnaireUrl: url,
          questionnaires: data.questionnaires,
        };
      }
      if (unsettled.size > 0) setEscalationErrors(unsettled);
      // No locally-decodable crisis answer to re-save (revised down): hold the
      // crisis screen on the server's recorded obligation instead.
      setServerEscalationPending(unsettled.size === 0);
    } else {
      setServerEscalationPending(false);
    }

    walkRef.current = {
      sections: data.sections,
      questionnaires: data.questionnaires,
    };

    // Server-authoritative completion (§8.20): a link re-opened after the
    // administration has fully settled shows the results summary again — with
    // the crisis screen first when a self-harm answer was given (evaluated on
    // the FRESH answers, §9.17, OR'd with the server's recorded safety signal:
    // a revised-down answer never erases a recorded escalation, F4/F8) — never
    // the last question. Once the patient has acknowledged the crisis screen
    // this pass, present the results.
    const crisisPresent =
      hasCrisisAnswer(restored) || data.safety?.triggered === true;
    if (data.resume?.complete) {
      if (crisisPresent && !crisisAckRef.current) {
        setState({ status: "crisis" });
      } else {
        const cards = buildResultCards(
          data.sections,
          data.questionnaires,
          restored,
          data.sectionStates,
        );
        setState({
          status: "results",
          cards,
          crisisTriggered: crisisAckRef.current,
        });
      }
      crisisAckRef.current = false;
      return;
    }
    crisisAckRef.current = false;

    const steps = buildScreenerSteps(
      data.sections,
      data.questionnaires,
      restored,
      settledSectionInstrumentUrls(data.sections, restoredSettlements),
    );
    // Fail visible when the server's resume target has no unpresented step in
    // the local walk (F9): server and client requiredness should always agree
    // post-alignment, so a divergence means this client cannot faithfully
    // render the administration — an explicit error beats an endless re-sync
    // loop or silently walking a different instrument than the server expects.
    const resumeUrl = data.resume?.questionnaireUrl ?? null;
    if (resumeUrl !== null) {
      const representable = steps.some((step) => {
        if (step.questionnaireUrl !== resumeUrl) return false;
        const linkId = step.item.linkId ?? "";
        const answered = restored[resumeUrl]?.[linkId] !== undefined;
        const skipped = (restoredSkips[resumeUrl] ?? []).includes(linkId);
        return !answered && !skipped;
      });
      if (!representable) {
        console.error(
          "Screener resume target is not representable in the local walk",
          resumeUrl,
        );
        setState({ status: "kind-error" });
        return;
      }
    }
    // A truly fresh administration — nothing answered, skipped, or settled —
    // opens on the welcome screen (parity with the fixed-survey flow). Any
    // recorded work means the patient is resuming, so go straight to the walk;
    // and once Start is pressed the welcome is never shown again this session.
    const isFresh =
      Object.keys(restored).length === 0 &&
      Object.keys(restoredSkips).length === 0 &&
      Object.keys(restoredSettlements).length === 0;
    if (isFresh && steps.length > 0 && !welcomeAcknowledgedRef.current) {
      setState({
        status: "welcome",
        sections: data.sections,
        questionnaires: data.questionnaires,
        kind: data.kind,
        context: { advocateDisplayName: data.advocateDisplayName },
      });
      return;
    }
    setState({
      status: "assessment",
      sections: data.sections,
      questionnaires: data.questionnaires,
      stepIndex: firstUnpresentedStepIndex(steps, restored, restoredSkips),
      context: {
        advocateDisplayName: data.advocateDisplayName,
      },
    });
  }, []);

  const loadState = useCallback(
    async (activeToken: string) => {
      setVerifiedToken(activeToken);
      let data;
      try {
        data = await getScreenerState(activeToken);
      } catch (err) {
        // A failed state load must not strand the patient on the spinner:
        // surface a terminal/error screen (a dead token ends the session;
        // anything else is a generic retryable error).
        setState(surveyErrorToState(err, "We couldn't load your screener"));
        return;
      }
      applyState(data);
    },
    [applyState],
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    validateScreener(token)
      .then((data) => {
        if (cancelled) return;
        if (data.requiresBirthdayVerification) {
          setState({ status: "verify-birthday" });
        } else {
          // Already a verified token in the URL — go straight to state.
          void loadState(token);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState(surveyErrorToState(err, "Something went wrong"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, loadState]);

  const handleBirthdayVerify = useCallback(
    async (birthDate: string) => {
      if (!token) return;
      const data = await verifyScreenerBirthday(token, birthDate);
      await loadState(data.token);
    },
    [token, loadState],
  );

  const handleBirthdayError = useCallback((error: unknown) => {
    setState(surveyErrorToState(error, "Verification failed"));
  }, []);

  const handleStartWelcome = useCallback(() => {
    welcomeAcknowledgedRef.current = true;
    setState((prev) => {
      if (prev.status !== "welcome") return prev;
      return {
        status: "assessment",
        sections: prev.sections,
        questionnaires: prev.questionnaires,
        stepIndex: 0,
        context: prev.context,
      };
    });
  }, []);

  const steps = useMemo<ScreenerStep[]>(() => {
    if (state.status !== "assessment") return [];
    return buildScreenerSteps(
      state.sections,
      state.questionnaires,
      answers,
      settledSectionInstrumentUrls(state.sections, settlements),
    );
  }, [state, answers, settlements]);

  // A single FIFO-serialized runner for answer saves. Serializing all saves
  // through one chain guarantees an earlier, slower POST never lands after a
  // later one (D6 / no lost answers), and per-instrument error tracking keeps
  // one failure from clearing another.
  const enqueueSave = useCallback(
    (write: PendingSave): Promise<void> => {
      const url = write.questionnaireUrl;
      writesRef.current[url] = write;
      const run = async () => {
        const activeToken = verifiedToken ?? token;
        if (!activeToken) return;
        markSaving(url, true);
        let failed = false;
        try {
          // Read the freshest answers at execution time (not a frozen
          // snapshot): serialized with the union-by-linkId server merge, a
          // retry or slower earlier save can never revert a newer answer.
          const current = answersRef.current;
          const row = current[url] ?? {};
          // Only active (non-gated) answered items are sent (D5). Completion is
          // decided by the server from the merged set, so no client flag.
          const active = activeInstrumentItems(
            url,
            write.questionnaires,
            current,
          );
          const items = active
            .filter((qi) => row[qi.linkId ?? ""] !== undefined)
            .map((qi) => buildSurveyResponseItem(qi, row[qi.linkId ?? ""]));
          // Explicit skips ride the same FIFO save (§8.4): the instrument's
          // current skip set minus anything since answered (answers win). A
          // skip-only save (no items) is valid.
          const skippedLinkIds = (skipsRef.current[url] ?? []).filter(
            (linkId) => row[linkId] === undefined,
          );
          if (items.length === 0 && skippedLinkIds.length === 0) return;
          const res = await saveScreenerAnswer(
            activeToken,
            url,
            items,
            skippedLinkIds,
          );
          updateSaveError(url, undefined);
          // A positive self-harm save reports whether its escalation was
          // delivered; an undelivered one holds the crisis screen for retry.
          if (res.safetyTriggered) {
            setEscalationErrors((prev) => {
              const next = new Set(prev);
              if (res.escalationDelivered === false) {
                next.add(url);
              } else {
                next.delete(url);
              }
              return next;
            });
          }
        } catch (err) {
          // A dead/expired token — or a closed administration (D11) — is
          // terminal; end the session visibly on the mapped screen.
          if (
            err instanceof SurveyError &&
            (err.reason === "expired" ||
              err.reason === "invalid" ||
              err.reason === "closed")
          ) {
            setState(surveyErrorToState(err, "Your session has ended"));
            return;
          }
          // The write raced a settlement/completion (stale view): the server's
          // truth wins (D10/D18) — re-sync instead of parking a permanent,
          // unretryable save error.
          if (
            err instanceof SurveyError &&
            (err.reason === "settled" || err.reason === "completed")
          ) {
            void loadState(activeToken);
            return;
          }
          // Non-terminal failure (D6): keep the state on screen and record a
          // per-instrument, non-destructive retry rather than silently dropping
          // the write (a dropped phq9-9 save would drop the safety escalation).
          failed = true;
          updateSaveError(url, write);
        } finally {
          markSaving(url, false);
          // A stalled completion resumes the moment every save has settled
          // cleanly — no second click required (Bugbot #1576).
          if (
            !failed &&
            pendingFinalizeRef.current &&
            savingUrlsRef.current.size === 0 &&
            Object.keys(saveErrorsRef.current).length === 0
          ) {
            pendingFinalizeRef.current = false;
            finalizeRef.current?.(false);
          }
        }
      };
      // Chain onto the FIFO queue; a failure never blocks later writes.
      const next = saveChainRef.current.then(run, run);
      saveChainRef.current = next.catch(() => {});
      return next;
    },
    [token, verifiedToken, loadState, markSaving, updateSaveError],
  );

  const retrySave = useCallback(() => {
    for (const write of Object.values(saveErrors)) {
      void enqueueSave(write);
    }
  }, [saveErrors, enqueueSave]);

  // Re-drive the escalation for every crisis instrument that is still unsettled
  // — whether its save failed (saveErrors) or its save succeeded but the server
  // reported the escalation undelivered (escalationErrors). The escalation is
  // idempotent, so re-saving the same answers re-attempts it exactly once.
  const retryEscalation = useCallback(() => {
    let enqueued = false;
    for (const url of crisisInstrumentUrls(answersRef.current)) {
      if (savingUrls.has(url)) continue;
      const write = saveErrors[url] ?? writesRef.current[url];
      if (write) {
        void enqueueSave(write);
        enqueued = true;
      }
    }
    // No local write carries the crisis answer (it was revised down): re-fetch
    // state so the server re-drives the recorded escalation and reports fresh
    // delivery (F4). The crisis screen must NEVER be replaced by a generic
    // error while the escalation is pending — a failed or fail-visible
    // (kindError) fetch keeps the hold and its 988 resources on screen; only a
    // definitive fresh state re-routes.
    if (!enqueued && serverEscalationPending) {
      const activeToken = verifiedToken ?? token;
      if (!activeToken) return;
      void (async () => {
        try {
          const data = await getScreenerState(activeToken);
          if (!data.kindError) {
            applyState(data);
          }
        } catch {
          // Keep the crisis hold; the retry control stays available.
        }
      })();
    }
  }, [
    saveErrors,
    savingUrls,
    enqueueSave,
    serverEscalationPending,
    token,
    verifiedToken,
    applyState,
  ]);

  const handleAnswer = useCallback(
    (
      questionnaireUrl: string,
      linkId: string,
      value: string,
      questionnaires: Questionnaire[],
    ) => {
      // The walk is frozen while the end-of-walk re-sync runs (a late write
      // would be overwritten by the restore before its save executes).
      if (finalizingRef.current) return;
      // A fresh mutation withdraws any stalled completion request — only an
      // explicit end-of-walk action re-arms it.
      pendingFinalizeRef.current = false;
      clearAdvanceTimer();
      const prev = answersRef.current;
      const next: ScreenerAnswers = {
        ...prev,
        [questionnaireUrl]: {
          ...(prev[questionnaireUrl] ?? {}),
          [linkId]: value,
        },
      };
      answersRef.current = next;
      setAnswers(next);
      // A gate-opening answer at the LAST step reveals new questions the
      // question component gives no forward control for (auto-advance skips
      // the last question, and Submit disappears once the walk grows): advance
      // into the revealed portion on the same beat auto-advance uses (F15). An
      // answer that does not grow the walk keeps the normal Submit behavior.
      const prevState = stateRef.current;
      if (prevState.status === "assessment") {
        const settled = settledSectionInstrumentUrls(
          prevState.sections,
          settlementsRef.current,
        );
        const before = buildScreenerSteps(
          prevState.sections,
          prevState.questionnaires,
          prev,
          settled,
        );
        const after = buildScreenerSteps(
          prevState.sections,
          prevState.questionnaires,
          next,
          settled,
        );
        if (
          after.length > before.length &&
          prevState.stepIndex >= before.length - 1
        ) {
          advanceTimerRef.current = setTimeout(() => {
            advanceTimerRef.current = null;
            goNextRef.current?.();
          }, 350);
        }
      }
      // An answer always wins over an earlier skip of the same item (§8.16
      // one-step-back): drop it locally; the next save's skippedLinkIds
      // excludes answered ids, and the server enforces the same rule.
      if ((skipsRef.current[questionnaireUrl] ?? []).includes(linkId)) {
        const nextSkips: ScreenerSkips = {
          ...skipsRef.current,
          [questionnaireUrl]: (skipsRef.current[questionnaireUrl] ?? []).filter(
            (id) => id !== linkId,
          ),
        };
        skipsRef.current = nextSkips;
        setSkips(nextSkips);
      }
      // Persist the moment the answer is given (D6); the queued save reads the
      // fresh snapshot at execution time.
      void enqueueSave({ questionnaireUrl, questionnaires });
    },
    [enqueueSave, clearAdvanceTimer],
  );

  // A self-harm escalation is unsettled while its instrument's save is still in
  // flight or has failed. The crisis screen must not present as done — and the
  // advocate Task fires only on a successful server save — until it settles.
  const escalationUnsettled =
    serverEscalationPending ||
    crisisInstrumentUrls(answers).some(
      (url) =>
        savingUrls.has(url) ||
        Boolean(saveErrors[url]) ||
        escalationErrors.has(url),
    );

  /**
   * End-of-walk re-sync (D18/D12): before presenting anything terminal, ride
   * the FIFO chain (so every pending save/skip has landed) and fetch fresh
   * server state; then route on the SERVER's entry — closed → expired,
   * completed-but-unstarted → the closing card (no results), completed →
   * results, still-in-progress → re-sync the walk. A fetch failure holds with
   * a retry; the client never fabricates a terminal screen.
   */
  const finalizeWalk = useCallback(
    (crisisAcknowledged: boolean) => {
      const activeToken = verifiedToken ?? token;
      if (!activeToken || finalizingRef.current) return;
      finalizingRef.current = true;
      setFinalizing(true);
      setFinalizeError(false);
      // Failed writes get one more chance FIRST: their retries are enqueued
      // ahead of this finalize on the FIFO chain, so a recovered save lets the
      // completion proceed in one pass (Bugbot #1576: crisis Continue must
      // never silently no-op on a failed non-crisis save).
      if (Object.keys(saveErrorsRef.current).length > 0) {
        for (const write of Object.values(saveErrorsRef.current)) {
          void enqueueSave(write);
        }
      }
      const run = async () => {
        try {
          // Re-check at EXECUTION time (the chain has flushed by now): a save
          // that is STILL failed must abort this finalize — a re-sync would
          // discard the locally-held answer/skip the retry alert is protecting
          // (D6). The abort is visible (finalizeError) and the completion
          // re-arms so a later successful retry resumes it automatically.
          if (Object.keys(saveErrorsRef.current).length > 0) {
            pendingFinalizeRef.current = true;
            setFinalizeError(true);
            return;
          }
          const data = await getScreenerState(activeToken);
          crisisAckRef.current = crisisAcknowledged;
          pendingFinalizeRef.current = false;
          // One routing point for every fresh state (D18): applyState renders
          // the server's entry — closed → expired, completed-unstarted →
          // closing card, completed → crisis-first results evaluated on the
          // FRESH answers, still-in-progress → walk re-sync.
          applyState(data);
        } catch (err) {
          if (
            err instanceof SurveyError &&
            (err.reason === "expired" ||
              err.reason === "invalid" ||
              err.reason === "closed")
          ) {
            setState(surveyErrorToState(err, "Your session has ended"));
            return;
          }
          setFinalizeError(true);
        } finally {
          finalizingRef.current = false;
          setFinalizing(false);
        }
      };
      const next = saveChainRef.current.then(run, run);
      saveChainRef.current = next.catch(() => {});
    },
    [token, verifiedToken, applyState, enqueueSave],
  );
  useEffect(() => {
    finalizeRef.current = finalizeWalk;
  }, [finalizeWalk]);

  const goNext = useCallback(() => {
    const prev = stateRef.current;
    if (prev.status !== "assessment") return;
    const rebuilt = buildScreenerSteps(
      prev.sections,
      prev.questionnaires,
      answersRef.current,
      settledSectionInstrumentUrls(prev.sections, settlementsRef.current),
    );
    if (prev.stepIndex >= rebuilt.length - 1) {
      // §8.21: a full-screen crisis screen precedes results whenever a
      // positive self-harm answer was given during the administration. Safety
      // display never waits on a fetch — the crisis screen enters immediately
      // and carries its own unsettled-save/undelivered-escalation handling.
      if (hasCrisisAnswer(answersRef.current)) {
        setState({ status: "crisis" });
        return;
      }
      // Do not present the completed summary while a save is still in flight
      // or has failed: hold on the final question until every save settles
      // (the save-retry alert stays visible for failures) — and re-arm the
      // completion so it resumes automatically when the last save lands.
      const hasUnsettledSave =
        savingUrlsRef.current.size > 0 ||
        Object.keys(saveErrorsRef.current).length > 0;
      if (hasUnsettledSave) {
        pendingFinalizeRef.current = true;
        return;
      }
      finalizeWalk(false);
      return;
    }
    setState({ ...prev, stepIndex: prev.stepIndex + 1 });
  }, [finalizeWalk]);
  useEffect(() => {
    goNextRef.current = goNext;
  }, [goNext]);

  const acknowledgeCrisis = useCallback(() => {
    if (stateRef.current.status !== "crisis") return;
    finalizeWalk(true);
  }, [finalizeWalk]);

  const goBack = useCallback(() => {
    // Stepping back withdraws a stalled completion request: the patient is
    // revising, so a late-settling save must not auto-finalize under them.
    pendingFinalizeRef.current = false;
    clearAdvanceTimer();
    setState((prev) => {
      if (prev.status !== "assessment" || prev.stepIndex <= 0) return prev;
      return { ...prev, stepIndex: prev.stepIndex - 1 };
    });
  }, [clearAdvanceTimer]);

  // Per-question skip (§8.4): record the skip locally (walk position is UX),
  // persist it through the FIFO save chain (asked-and-not-answered — never an
  // answer value, D3/§9.16), and advance immediately.
  const handleSkip = useCallback(
    (step: ScreenerStep, questionnaires: Questionnaire[]) => {
      if (finalizingRef.current) return;
      pendingFinalizeRef.current = false;
      clearAdvanceTimer();
      const url = step.questionnaireUrl;
      const linkId = step.item.linkId ?? "";
      if (!linkId) return;
      if (!(skipsRef.current[url] ?? []).includes(linkId)) {
        const nextSkips: ScreenerSkips = {
          ...skipsRef.current,
          [url]: [...(skipsRef.current[url] ?? []), linkId],
        };
        skipsRef.current = nextSkips;
        setSkips(nextSkips);
      }
      void enqueueSave({ questionnaireUrl: url, questionnaires });
      goNext();
    },
    [enqueueSave, goNext, clearAdvanceTimer],
  );

  // One-action section decline (§8.5–§8.6, D10): the server settles the
  // section and derives the recorded kind; the client advances from the
  // RESPONSE's truth (D18), never a local recomputation. Rides the FIFO chain
  // so it lands after this sitting's pending answer saves (a mid-section
  // decline keeps the answers already given).
  const handleDeclineSection = useCallback(
    (step: ScreenerStep) => {
      const activeToken = verifiedToken ?? token;
      if (!activeToken || finalizingRef.current) return;
      clearAdvanceTimer();
      // A FAILED save for this section must be retried BEFORE the section can
      // settle: a decline that lands without those answers would record
      // Skipped instead of Partial — declined and the retried answer would
      // then be refused as settled — losing given answers (§8.6/D6). In-flight
      // saves are fine (the settle rides the FIFO chain behind them).
      const section =
        stateRef.current.status === "assessment"
          ? stateRef.current.sections.find((s) => s.key === step.sectionKey)
          : undefined;
      const sectionUrls = new Set(
        (section?.instruments ?? []).map((i) => i.questionnaireUrl),
      );
      const hasFailedSectionSave = Object.keys(saveErrorsRef.current).some(
        (url) => sectionUrls.has(url),
      );
      if (hasFailedSectionSave) {
        setDeclineError(
          "An answer in this section hasn't saved yet. Retry saving it first, then skip the section.",
        );
        return;
      }
      setDeclineError(null);
      const run = async () => {
        try {
          // Re-check at EXECUTION time (the chain has flushed): a save for this
          // section that failed after the decline was queued must abort the
          // settle — settling now would record Skipped and orphan the unsaved
          // answer behind a settled section (§8.6/D6).
          if (
            Object.keys(saveErrorsRef.current).some((url) =>
              sectionUrls.has(url),
            )
          ) {
            setDeclineError(
              "An answer in this section hasn't saved yet. Retry saving it first, then skip the section.",
            );
            return;
          }
          const res: ScreenerSettleResponse = await settleScreenerSection(
            activeToken,
            step.sectionKey,
          );
          const mergedSettlements = {
            ...settlementsRef.current,
            ...(res.settlements ?? {}),
          };
          settlementsRef.current = mergedSettlements;
          setSettlements(mergedSettlements);
          const prev = stateRef.current;
          if (prev.status !== "assessment") return;
          if (res.entry === "completed") {
            // The decline completed the walk. D12: a never-started
            // administration shows the closing card (no results, no crisis —
            // no answers exist). Otherwise finalize routes through fresh
            // server state, which puts the crisis screen first when a
            // self-harm answer exists (§8.6/§9.17).
            if (res.started === false) {
              setState({ status: "completed-unstarted" });
              return;
            }
            finalizeWalk(false);
            return;
          }
          // Advance from the RESPONSE's resume target (D18) — the first
          // unpresented step of the instrument the server named. A target the
          // local walk cannot represent means the client is stale: re-sync to
          // the server's truth rather than guessing a position. The local
          // first-unpresented fallback applies only when the response names no
          // target at all.
          const rebuilt = buildScreenerSteps(
            prev.sections,
            prev.questionnaires,
            answersRef.current,
            settledSectionInstrumentUrls(prev.sections, mergedSettlements),
          );
          const resumeUrl = res.resume?.questionnaireUrl ?? null;
          if (resumeUrl !== null) {
            const serverIndex = rebuilt.findIndex((s) => {
              if (s.questionnaireUrl !== resumeUrl) return false;
              const linkId = s.item.linkId ?? "";
              const answered =
                answersRef.current[resumeUrl]?.[linkId] !== undefined;
              const skipped = (skipsRef.current[resumeUrl] ?? []).includes(
                linkId,
              );
              return !answered && !skipped;
            });
            if (serverIndex === -1) {
              await loadState(activeToken);
              return;
            }
            setState({ ...prev, stepIndex: serverIndex });
            return;
          }
          setState({
            ...prev,
            stepIndex: firstUnpresentedStepIndex(
              rebuilt,
              answersRef.current,
              skipsRef.current,
            ),
          });
        } catch (err) {
          if (err instanceof SurveyError) {
            // A closed administration is terminal (D11 — expired screen).
            if (
              err.reason === "closed" ||
              err.reason === "expired" ||
              err.reason === "invalid"
            ) {
              setState(surveyErrorToState(err, "Your session has ended"));
              return;
            }
            // A stale view (already settled / already complete): re-sync to
            // the server's truth instead of erroring.
            if (err.reason === "settled" || err.reason === "completed") {
              void loadState(activeToken);
              return;
            }
          }
          // Non-terminal failure: keep the question on screen with a
          // non-destructive retry (nothing was settled).
          setDeclineError(
            "Nothing was changed. Check your connection and try again.",
          );
        }
      };
      const next = saveChainRef.current.then(run, run);
      saveChainRef.current = next.catch(() => {});
    },
    [token, verifiedToken, loadState, finalizeWalk, clearAdvanceTimer],
  );

  const renderAssessment = () => {
    if (state.status !== "assessment") return null;
    // Freeze the walk while completion is being confirmed against the server:
    // no controls, so no late mutation can race the re-sync (D6/D18).
    if (finalizing) {
      return (
        <Center py="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text style={{ opacity: 0.5, color: "#2a1a2e" }}>
              Confirming your responses…
            </Text>
          </Stack>
        </Center>
      );
    }
    const stepIndex = Math.min(state.stepIndex, Math.max(0, steps.length - 1));
    const step = steps[stepIndex];
    if (!step) {
      // An empty walk with unsettled work is a RECOVERABLE state, not a dead
      // end (Bugbot #1576: a last-section decline after a failed save landed
      // here as "Screener unavailable"): keep the retry affordances visible.
      // The completion is already re-armed by whichever path stalled it (the
      // finalize abort or the goNext hold), so a successful retry resumes it.
      if (
        Object.keys(saveErrors).length > 0 ||
        finalizeError ||
        savingUrls.size > 0
      ) {
        return (
          <Stack gap="md">
            {Object.keys(saveErrors).length > 0 ? (
              <Alert
                color="orange"
                variant="light"
                title="We couldn't save your last answer"
              >
                <Stack gap="xs">
                  <Text size="sm" style={{ color: "#2a1a2e" }}>
                    Your answer is still here. Check your connection and try
                    again.
                  </Text>
                  <Button size="xs" variant="light" onClick={retrySave}>
                    Try again
                  </Button>
                </Stack>
              </Alert>
            ) : finalizeError ? (
              <Alert
                color="orange"
                variant="light"
                title="We couldn't confirm your responses"
              >
                <Stack gap="xs">
                  <Text size="sm" style={{ color: "#2a1a2e" }}>
                    Your answers are saved. Check your connection and try again.
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => finalizeWalk(false)}
                  >
                    Try again
                  </Button>
                </Stack>
              </Alert>
            ) : (
              <LoadingState />
            )}
          </Stack>
        );
      }
      return (
        <MessageCard
          title="Screener unavailable"
          message="We couldn't load your screener. Please contact your care team."
          color="red"
          contactCareTeam
        />
      );
    }
    const linkId = step.item.linkId ?? "";
    const isDepression =
      step.questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9 ||
      step.questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9A;
    const isCrisisQuestion = isDepression && SELF_HARM_LINK_IDS.has(linkId);
    const currentValue = answers[step.questionnaireUrl]?.[linkId];
    const showCrisisAlert =
      isCrisisQuestion && POSITIVE_SELF_HARM_VALUES.has(currentValue ?? "");
    const isLast = stepIndex === steps.length - 1;

    return (
      <Stack gap="md">
        {Object.keys(saveErrors).length > 0 && (
          <Alert
            color="orange"
            variant="light"
            title="We couldn't save your last answer"
          >
            <Stack gap="xs">
              <Text size="sm" style={{ color: "#2a1a2e" }}>
                Your answer is still here. Check your connection and try again.
              </Text>
              <Button size="xs" variant="light" onClick={retrySave}>
                Try again
              </Button>
            </Stack>
          </Alert>
        )}
        {declineError !== null && (
          <Alert
            color="orange"
            variant="light"
            title="We couldn't skip this section"
          >
            <Stack gap="xs">
              <Text size="sm" style={{ color: "#2a1a2e" }}>
                {declineError}
              </Text>
              <Button
                size="xs"
                variant="light"
                onClick={() => handleDeclineSection(step)}
              >
                Try again
              </Button>
            </Stack>
          </Alert>
        )}
        {finalizeError && (
          <Alert
            color="orange"
            variant="light"
            title="We couldn't confirm your responses"
          >
            <Stack gap="xs">
              <Text size="sm" style={{ color: "#2a1a2e" }}>
                Your answers are saved. Check your connection and try again.
              </Text>
              <Button
                size="xs"
                variant="light"
                onClick={() => finalizeWalk(false)}
              >
                Try again
              </Button>
            </Stack>
          </Alert>
        )}
        <LinearAssessmentView
          key={`${step.questionnaireUrl}-${linkId}`}
          sectionLabel={step.sectionLabel}
          questionOrdinal={step.questionOrdinal}
          totalQuestions={step.totalQuestions}
          instructions={step.instructions}
          item={step.item}
          value={currentValue}
          advocateFooterName={
            state.context.advocateDisplayName ?? getAdvocateDisplayName()
          }
          onAnswer={(url, id, value) =>
            handleAnswer(url, id, value, state.questionnaires)
          }
          onBack={goBack}
          onNext={goNext}
          questionnaireUrl={step.questionnaireUrl}
          canGoBack={stepIndex > 0}
          isLastQuestion={isLast}
          showCrisisAlert={showCrisisAlert}
          crisisAlertThresholds={
            isCrisisQuestion ? POSITIVE_SELF_HARM_VALUE_LIST : undefined
          }
          onSkip={() => handleSkip(step, state.questionnaires)}
          onDeclineSection={() => handleDeclineSection(step)}
          declineSectionLabel={step.sectionLabel}
        />
      </Stack>
    );
  };

  return (
    <SurveyShell>
      {state.status === "loading" && <LoadingState />}
      {state.status === "invalid" && (
        <MessageCard
          title="Invalid link"
          message="This screener link is not valid. Please check the link from your text message and try again."
          color="red"
          contactCareTeam
        />
      )}
      {state.status === "expired" && (
        <MessageCard
          title="Link expired"
          message="This screener link has expired and can no longer be used."
          color="orange"
          contactCareTeam
        />
      )}
      {state.status === "already-completed" && (
        <MessageCard
          title="Already completed"
          message="Thank you — this screener has already been submitted. No further action is needed."
          color="blue"
        />
      )}
      {state.status === "completed-unstarted" && (
        <MessageCard
          title="Screener closed"
          message="This screener was closed without any answers, so there are no results to show. No further action is needed."
          color="blue"
          contactCareTeam
        />
      )}
      {state.status === "ineligible" && (
        <MessageCard
          title="Screener unavailable"
          message="This screener isn't available for this account. Please contact your care team if you have questions."
          color="orange"
          contactCareTeam
        />
      )}
      {state.status === "kind-error" && (
        <MessageCard
          title="We couldn't start your screener"
          message="Something went wrong preparing your screener. Please try again, or contact your care team."
          color="orange"
          contactCareTeam
        />
      )}
      {state.status === "error" && (
        <MessageCard
          title="Something went wrong"
          message={state.message}
          color="red"
        />
      )}
      {state.status === "verify-birthday" && (
        <BirthdayVerification
          onError={handleBirthdayError}
          onVerify={handleBirthdayVerify}
        />
      )}
      {state.status === "welcome" && (
        <ScreenerWelcomeScreen
          kind={state.kind}
          advocateDisplayName={state.context.advocateDisplayName}
          onStart={handleStartWelcome}
        />
      )}
      {state.status === "assessment" && renderAssessment()}
      {state.status === "crisis" && (
        <CrisisScreen
          onContinue={acknowledgeCrisis}
          escalationUnsettled={escalationUnsettled}
          onRetry={retryEscalation}
          resultsUnavailable={finalizeError}
        />
      )}
      {state.status === "results" && (
        <ScreenerResults
          cards={state.cards}
          crisisTriggered={state.crisisTriggered}
        />
      )}
    </SurveyShell>
  );
};

/**
 * Full-screen crisis screen shown before results whenever a positive self-harm
 * answer was given (PRD §8.21). Crisis resources are shown unconditionally
 * (safety-first). The advocate escalation fires on the successful server save,
 * so while that save is unsettled the screen keeps a visible retry and holds
 * the patient here rather than presenting the flow as done with a dropped
 * escalation (D2/D14).
 */
const CrisisScreen = ({
  onContinue,
  escalationUnsettled,
  onRetry,
  resultsUnavailable = false,
}: {
  onContinue: () => void;
  escalationUnsettled: boolean;
  onRetry: () => void;
  /** The post-crisis results re-sync failed; Continue retries it. */
  resultsUnavailable?: boolean;
}) => (
  <Paper radius="lg" p="xl" shadow="sm">
    <Stack align="center" gap="md">
      <ThemeIcon size="xl" radius="xl" color="red" variant="light">
        !
      </ThemeIcon>
      <Title order={2} ta="center" c="#391640">
        Support is available right now
      </Title>
      <Text ta="center" maw={440} style={{ color: "#2a1a2e" }}>
        Some of your answers show you may be going through a very hard time. You
        are not alone, and help is available any time.
      </Text>
      <Text ta="center" maw={440} fw={600} style={{ color: "#2a1a2e" }}>
        Call or text the 988 Suicide &amp; Crisis Lifeline (call or text 988).
        If you are in immediate danger, call 911 or go to the nearest emergency
        room.
      </Text>
      {escalationUnsettled ? (
        <Stack align="center" gap="xs" mt="sm">
          <Text size="sm" ta="center" maw={440} style={{ color: "#2a1a2e" }}>
            We're still saving your responses so your care team is notified.
            Please check your connection and try again.
          </Text>
          <Button variant="light" onClick={onRetry}>
            Try again
          </Button>
        </Stack>
      ) : (
        <Stack align="center" gap="xs" mt="sm">
          {resultsUnavailable && (
            <Text size="sm" ta="center" maw={440} style={{ color: "#2a1a2e" }}>
              We couldn't load your results. Check your connection and try
              again.
            </Text>
          )}
          <Button onClick={onContinue}>Continue to your results</Button>
        </Stack>
      )}
    </Stack>
  </Paper>
);

const LoadingState = () => (
  <Center py="xl">
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text style={{ opacity: 0.5, color: "#2a1a2e" }}>
        Loading your screener…
      </Text>
    </Stack>
  </Center>
);

const MessageCard = ({
  title,
  message,
  color,
  contactCareTeam = false,
}: {
  title: string;
  message: string;
  color: string;
  contactCareTeam?: boolean;
}) => (
  <Paper radius="lg" p="xl" shadow="sm">
    <Stack align="center" gap="md">
      <ThemeIcon size="xl" radius="xl" color={color} variant="light">
        {color === "blue" ? "i" : "!"}
      </ThemeIcon>
      <Title order={2} ta="center" c="#391640">
        {title}
      </Title>
      <Text ta="center" maw={400} style={{ opacity: 0.55, color: "#2a1a2e" }}>
        {message}
      </Text>
      {contactCareTeam && (
        <Text ta="center" maw={400} fw={500} style={{ color: "#2a1a2e" }}>
          {CARE_TEAM_CONTACT_MESSAGE}
        </Text>
      )}
    </Stack>
  </Paper>
);
